import {
  accountSearchColumns,
  boPhanSearchColumns,
  chiPhiKhongHopLeSearchColumns,
  dotThanhToanSearchColumns,
  hopDongSearchColumns,
  kheUocSearchColumns,
  lenhSanXuatSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  thueSearchColumns,
  vatTu1SearchColumns,
  vatTuSearchColumns,
  viTriSearchColumns,
  vuViecSearchColumns,
  warehouseSearchColumns
} from '@/constants';
import {
  buildInputTableCols,
  CellFieldV2 as CellField,
  type DataTableCol,
  type DataTableColumn,
  DvtFieldV2,
  SearchFieldV2 as SearchField
} from '@/components/custom/erp';
import {
  BoPhan,
  ChiPhiKhongHopLeData,
  DotThanhToan,
  HopDong,
  KheUoc,
  KhoHang,
  Phi,
  TaiKhoan,
  Tax,
  VatTu,
  VuViec
} from '@/types/schemas';
import type { FormMode } from '@/types/form';

type DetailDataTableColumn = DataTableColumn & { pasteConfig?: BanHangValue; nonWritable?: boolean };
type DetailInputColumn = DataTableCol<BanHangRecord> & { pasteConfig?: BanHangValue };

const buildColumnsWithPasteConfig = (columns: DetailInputColumn[]): DetailDataTableColumn[] =>
  buildInputTableCols<BanHangRecord>(columns).map((column, index) => ({
    ...column,
    pasteConfig: columns[index]?.pasteConfig
  }));

export const getDetailTableColumns = (
  formMode: FormMode,
  onCellValueChange: (rowUuid: string, field: string, newValue: BanHangValue) => void,
  fetchDetail: (params: BanHangTableParams) => void,
  cellError?: (rowUuid: string, field: string) => string | undefined
): DetailDataTableColumn[] => {
  const isViewMode = formMode === 'view';

  return buildColumnsWithPasteConfig([
    {
      field: 'ma_vt',
      headerName: 'Mã sản phẩm',
      width: 200,
      renderCell: params => (
        <SearchField<VatTu>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
          searchColumns={vatTu1SearchColumns}
          columnDisplay='ma_vt'
          dialogTitle='Danh mục vật tư'
          value={params.row.ma_vt_data?.ma_vt || ''}
          onRowSelection={async (row: VatTu) => {
            const data = await fetchDetail(row?.uuid || '');
            onCellValueChange(params.row.uuid, 'ma_vt_data', data);
          }}
          className='w-full'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_vt',
        resultField: 'ma_vt_data',
        detailEndpoint: QUERY_KEYS.VAT_TU
      }
    },
    {
      field: 'ten_vt',
      headerName: 'Tên sản phẩm',
      width: 200,
      renderCell: params => (
        <CellField disabled name='ten_vt' type='text' value={params.row.ma_vt_data?.ten_vt || ''} />
      ),
      nonWritable: true
    },
    {
      field: 'dvt',
      headerName: 'Đvt',
      width: 120,
      renderCell: params => <DvtFieldV2 row={params.row} onCellValueChange={onCellValueChange} disabled={isViewMode} />,
      nonWritable: true
    },
    {
      field: 'ma_kho',
      headerName: 'Mã kho',
      width: 150,
      renderCell: params => (
        <SearchField<KhoHang>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.KHO_HANG}/`}
          searchColumns={warehouseSearchColumns}
          columnDisplay='ma_kho'
          dialogTitle='Danh mục kho hàng'
          value={params.row.ma_kho_data?.ma_kho || params.row.ma_vt_data?.ma_kho_data?.ma_kho || ''}
          onRowSelection={(row: BanHangRecord) => {
            onCellValueChange(params.row.uuid, 'ma_kho_data', row);
          }}
          className='w-full'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_kho',
        resultField: 'ma_kho_data',
        detailEndpoint: QUERY_KEYS.KHO_HANG
      }
    },
    {
      field: 'ma_vi_tri',
      headerName: 'Mã vị trí',
      width: 150,
      renderCell: params => (
        <SearchField<BanHangRecord>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VI_TRI_KHO_HANG}/`}
          searchColumns={viTriSearchColumns}
          columnDisplay='ma_vi_tri'
          dialogTitle='Danh mục vị trí'
          value={params.row.ma_vi_tri_data?.ma_vi_tri || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_vi_tri_data', row)}
          className='w-full'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_vi_tri',
        resultField: 'ma_vi_tri_data',
        detailEndpoint: QUERY_KEYS.VI_TRI_KHO_HANG
      }
    },
    {
      field: 'so_luong',
      headerName: 'Số lượng',
      width: 150,
      renderCell: params => (
        <CellField
          commitOnBlur
          disabled={isViewMode}
          name='so_luong'
          type='number'
          placeholder='0'
          value={params.row.so_luong}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'so_luong', newValue)}
        />
      )
    },
    {
      field: 'gia_nt2',
      headerName: 'Giá bán VND',
      width: 150,
      renderCell: params => (
        <CellField
          commitOnBlur
          disabled={isViewMode}
          name='gia_nt2'
          type='number'
          placeholder='0'
          value={params.row.gia_nt2}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'gia_nt2', newValue)}
        />
      )
    },
    {
      field: 'tien_nt2',
      headerName: 'Tiền VND',
      width: 186,
      renderCell: params => (
        <CellField
          commitOnBlur
          disabled={isViewMode}
          name='tien_nt2'
          type='number'
          placeholder='0'
          value={params.row.tien_nt2}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_nt2', newValue)}
        />
      )
    },
    {
      field: 'tl_ck',
      headerName: 'Tl ck(%)',
      width: 100,
      renderCell: params => (
        <CellField
          commitOnBlur
          disabled={isViewMode}
          name='tl_ck'
          type='number'
          placeholder='0'
          value={params.row.tl_ck}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'tl_ck', newValue)}
        />
      )
    },
    {
      field: 'ck_nt',
      headerName: 'Ch.khấu VND',
      width: 170,
      renderCell: params => (
        <CellField
          commitOnBlur
          disabled={isViewMode}
          name='ck_nt'
          type='number'
          placeholder='0'
          value={params.row.ck_nt}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'ck_nt', newValue)}
        />
      )
    },
    {
      field: 'ma_thue',
      headerName: 'Thuế suất',
      width: 200,
      renderCell: params => (
        <SearchField<Tax>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.THUE}/`}
          searchColumns={thueSearchColumns}
          columnDisplay='ten_thue'
          dialogTitle='Danh mục thuế suất'
          value={params.row.ma_thue_data?.ma_thue || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange?.(params.row.uuid, 'ma_thue_data', row)}
          className='w-full'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_thue',
        resultField: 'ma_thue_data',
        detailEndpoint: QUERY_KEYS.THUE
      }
    },
    {
      field: 'tk_thue_no',
      headerName: 'Tk thuế',
      width: 150,
      renderCell: params => (
        <SearchField<BanHangRecord>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
          searchColumns={accountSearchColumns}
          columnDisplay='code'
          dialogTitle='Danh mục tài khoản'
          value={params.row.tk_thue_no_data?.code || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'tk_thue_no_data', row)}
          className='w-full'
        />
      ),
      pasteConfig: {
        lookupKey: 'tk_thue_no',
        resultField: 'tk_thue_no_data',
        detailEndpoint: QUERY_KEYS.TAI_KHOAN
      }
    },
    {
      field: 'thue_suat',
      headerName: 'Thuế suất(%)',
      width: 120,
      renderCell: params => (
        <CellField
          disabled
          name='thue_suat'
          type='number'
          placeholder='0'
          value={params.row.ma_thue_data?.thue_suat}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'thue_suat', newValue)}
        />
      ),
      nonWritable: true
    },
    {
      field: 'thue_nt',
      headerName: 'Thuế VND',
      width: 180,
      renderCell: params => (
        <CellField
          commitOnBlur
          disabled={isViewMode}
          name='thue_nt'
          type='number'
          placeholder='0'
          value={params.row.thue_nt}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'thue_nt', newValue)}
        />
      )
    },
    {
      field: 'tk_du',
      headerName: 'Tk đối ứng',
      width: 150,
      renderCell: params => (
        <SearchField<TaiKhoan>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
          searchColumns={accountSearchColumns}
          columnDisplay='code'
          dialogTitle='Danh mục tài khoản'
          value={params.row.tk_du_data?.code || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'tk_du_data', row)}
          className='w-full'
        />
      ),
      pasteConfig: {
        lookupKey: 'tk_du',
        resultField: 'tk_du_data',
        detailEndpoint: QUERY_KEYS.TAI_KHOAN
      }
    },
    {
      field: 'tk_ck',
      headerName: 'Tk chiết khấu',
      width: 150,
      renderCell: params => (
        <SearchField<TaiKhoan>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
          searchColumns={accountSearchColumns}
          columnDisplay='code'
          dialogTitle='Danh mục tài khoản'
          value={params.row.tk_ck_data?.code || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'tk_ck_data', row)}
          className='w-full'
        />
      ),
      pasteConfig: {
        lookupKey: 'tk_ck',
        resultField: 'tk_ck_data',
        detailEndpoint: QUERY_KEYS.TAI_KHOAN
      }
    },

    {
      field: 'ma_bp',
      headerName: 'Bộ phận',
      width: 150,
      renderCell: params => (
        <SearchField<BoPhan>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.BO_PHAN}/`}
          searchColumns={boPhanSearchColumns}
          columnDisplay='ma_bp'
          dialogTitle='Danh mục bộ phận'
          value={params.row.ma_bp_data?.ma_bp || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_bp_data', row)}
          className='w-full'
          error={cellError?.(params.row.uuid, 'ma_bp')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_bp',
        resultField: 'ma_bp_data',
        detailEndpoint: QUERY_KEYS.BO_PHAN
      }
    },
    {
      field: 'ma_vv',
      headerName: 'Vụ việc',
      width: 150,
      renderCell: params => (
        <SearchField<VuViec>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VU_VIEC}/`}
          searchColumns={vuViecSearchColumns}
          columnDisplay='ma_vu_viec'
          dialogTitle='Danh mục vụ việc'
          value={params.row.ma_vv_data?.ma_vu_viec || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_vv_data', row)}
          className='w-full'
          error={cellError?.(params.row.uuid, 'ma_vv')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_vu_viec',
        resultField: 'ma_vv_data',
        detailEndpoint: QUERY_KEYS.VU_VIEC
      }
    },
    {
      field: 'ma_hd',
      headerName: 'Hợp đồng',
      width: 150,
      renderCell: params => (
        <SearchField<HopDong>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.HOP_DONG}/`}
          searchColumns={hopDongSearchColumns}
          columnDisplay='ma_hd'
          dialogTitle='Danh mục hợp đồng'
          value={params.row.ma_hd_data?.ma_hd || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_hd_data', row)}
          className='w-full'
          error={cellError?.(params.row.uuid, 'ma_hd')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_hd',
        resultField: 'ma_hd_data',
        detailEndpoint: QUERY_KEYS.HOP_DONG
      }
    },
    {
      field: 'ma_dtt',
      headerName: 'Đợt thanh toán',
      width: 150,
      renderCell: params => (
        <SearchField<DotThanhToan>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.DOT_THANH_TOAN}/`}
          searchColumns={dotThanhToanSearchColumns}
          columnDisplay='ma_dtt'
          dialogTitle='Danh mục đợt thanh toán'
          value={params.row.ma_dtt_data?.ma_dtt || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_dtt_data', row)}
          className='w-full'
          error={cellError?.(params.row.uuid, 'ma_dtt')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_dtt',
        resultField: 'ma_dtt_data',
        detailEndpoint: QUERY_KEYS.DOT_THANH_TOAN
      }
    },
    {
      field: 'ma_ku',
      headerName: 'Khế ước',
      width: 120,
      renderCell: params => (
        <SearchField<KheUoc>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.KHE_UOC}/`}
          searchColumns={kheUocSearchColumns}
          columnDisplay='ma_ku'
          dialogTitle='Danh mục khế ước'
          value={params.row.ma_ku_data?.ma_ku || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_ku_data', row)}
          error={cellError?.(params.row.uuid, 'ma_ku')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_ku',
        resultField: 'ma_ku_data',
        detailEndpoint: QUERY_KEYS.KHE_UOC
      }
    },
    {
      field: 'ma_phi',
      headerName: 'Phí',
      width: 120,
      renderCell: params => (
        <SearchField<Phi>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.PHI}/`}
          searchColumns={phiSearchColumns}
          columnDisplay='ma_phi'
          dialogTitle='Danh mục phí'
          value={params.row.ma_phi_data?.ma_phi || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_phi_data', row)}
          error={cellError?.(params.row.uuid, 'ma_phi')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_phi',
        resultField: 'ma_phi_data',
        detailEndpoint: QUERY_KEYS.PHI
      }
    },
    {
      field: 'ma_sp',
      headerName: 'Sản phẩm',
      width: 120,
      renderCell: params => (
        <SearchField<VatTu>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
          searchColumns={vatTuSearchColumns}
          columnDisplay='ma_vt'
          dialogTitle='Danh mục sản phẩm'
          value={params.row.ma_sp_data?.ma_vt || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_sp_data', row)}
          error={cellError?.(params.row.uuid, 'ma_sp')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_vt',
        resultField: 'ma_sp_data',
        detailEndpoint: QUERY_KEYS.VAT_TU
      }
    },
    {
      field: 'ma_lsx',
      headerName: 'Lệnh sản xuất',
      width: 120,
      renderCell: params => (
        <SearchField
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.LENH_SAN_XUAT}/`}
          searchColumns={lenhSanXuatSearchColumns}
          columnDisplay='ma_lsx'
          dialogTitle='Lệnh sản xuất'
          value={params.row.ma_lsx_data?.ma_lsx || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_lsx_data', row)}
          error={cellError?.(params.row.uuid, 'ma_lsx')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_lsx',
        resultField: 'ma_lsx_data',
        detailEndpoint: QUERY_KEYS.LENH_SAN_XUAT
      }
    },
    {
      field: 'ma_cp0',
      headerName: 'C/p không h/lệ',
      width: 150,
      renderCell: params => (
        <SearchField<ChiPhiKhongHopLeData>
          disabled={isViewMode}
          type='text'
          searchEndpoint={`/${QUERY_KEYS.CHI_PHI_KHONG_HOP_LE}/`}
          searchColumns={chiPhiKhongHopLeSearchColumns}
          columnDisplay='ma_cp_khl'
          dialogTitle='Danh mục chi phí không hợp lệ'
          value={params.row.ma_cp0_data?.ma_cp || ''}
          onRowSelection={(row: BanHangRecord) => onCellValueChange(params.row.uuid, 'ma_cp0_data', row)}
          error={cellError?.(params.row.uuid, 'ma_cp0')}
          errorDisplay='tooltip'
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_cp_khl',
        resultField: 'ma_cp0_data',
        detailEndpoint: QUERY_KEYS.CHI_PHI_KHONG_HOP_LE
      }
    },
    {
      field: 'id_hd',
      headerName: 'Số hóa đơn',
      width: 170,
      renderCell: params => params.row.id_hd?.so_ct
    },
    {
      field: 'line_dh',
      headerName: 'Dòng HĐ',
      width: 120,
      columnType: 'number',
      renderCell: params => params.row.line_hd?.line
    }
  ]);
};
